import { ScriptStoryboardView } from "@/features/opportunities/components/scripts/ScriptStoryboard/ScriptStoryboard";
import type { ScriptStoryboardModel } from "@/features/opportunities/components/scripts/ScriptStoryboard/ScriptStoryboard";
import type { StoryboardPanel } from "@/features/opportunities/api/opportunities.api";
import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import { useGeneratePersonaStoryboard } from "@/lib/queries/persona-opportunity-scripts.queries";

const STORYBOARD_STATUSES = ["NONE", "GENERATING", "READY", "FAILED"] as const;
type StoryboardStatus = (typeof STORYBOARD_STATUSES)[number];

export interface PersonaScriptStoryboardProps {
  script: PersonaScriptDto;
  setId: string;
  tenantId: string;
  /** Hub drawer header owns the sketch generate/regenerate button. */
  sketchGenerateExternal?: boolean;
  sketchPending?: boolean;
  labelClassName?: string;
}

function asStoryboardStatus(value: string | undefined): StoryboardStatus {
  return STORYBOARD_STATUSES.includes(value as StoryboardStatus)
    ? (value as StoryboardStatus)
    : "NONE";
}

function signedStoryboardImages(
  images: PersonaScriptDto["storyboardImages"],
): ScriptStoryboardModel["storyboardImages"] {
  if (!Array.isArray(images)) return [];
  return images.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const record = item as { model?: unknown; imageUrl?: unknown };
    if (typeof record.model !== "string") return [];
    const imageUrl = typeof record.imageUrl === "string" ? record.imageUrl : null;
    return [{ model: record.model, imageUrl }];
  });
}

function toScriptStoryboardModel(script: PersonaScriptDto): ScriptStoryboardModel {
  return {
    id: script.id,
    hook: script.hook,
    scenes: script.scenes.map((scene) => ({
      id: scene.id,
      scene_number: scene.sceneNumber,
      scene_name: scene.sceneName,
      props: scene.props,
      seeing: scene.seeing,
      hearing: scene.hearing,
      product: scene.product ?? undefined,
    })),
    storyboardStatus: asStoryboardStatus(script.storyboardStatus),
    storyboardImages: signedStoryboardImages(script.storyboardImages),
    storyboardPanelStatus: asStoryboardStatus(script.storyboardPanelStatus),
    storyboardPanels: (script.storyboardPanels ?? []) as StoryboardPanel[],
  };
}

export function PersonaScriptStoryboard({
  script,
  setId,
  tenantId,
  sketchGenerateExternal = false,
  sketchPending = false,
  labelClassName,
}: PersonaScriptStoryboardProps) {
  const { mutate, isPending } = useGeneratePersonaStoryboard(setId);
  return (
    <ScriptStoryboardView
      script={toScriptStoryboardModel(script)}
      isPending={isPending}
      sketchGenerateExternal={sketchGenerateExternal}
      sketchPending={sketchPending}
      labelClassName={labelClassName}
      defaultCollapsed
      generate={(variant) => mutate({ scriptId: script.id, tenantId, variant })}
    />
  );
}
